import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ColorPicker } from "@vtaits/react-color-picker";
import "@vtaits/react-color-picker/index.css";

// actions
import actions from "../../../../../store/actions";

//
import { DEFAULT_PLANET_FIELDS, PLANET_ASPECTS, PLANET_LINE_COLORS, icons } from "helpers/constants";
import useOutside from "../../../../../hooks/useOutside";
import { debounce } from "lodash";
import { tvChartGetStyleByNumber } from "helpers/common";

const defaultColor = "rgba( 21, 119, 96, 1)";

const PlanetPop = ({
  planetPopup,
  setPlanetPopup,
  handlePlanetPop,
  onChange,
  data,
  ...props
}) => {
  const wrapperRef = useRef();
  const colorChange = useRef();

  const dispatch = useDispatch();

  const { planets } = useSelector((state) => state.workbook.workbook);

  const [showColorPicker, setColorPicker] = useState(false);
  const [planet, setPlanet] = useState(DEFAULT_PLANET_FIELDS);
  const [color, setColor] = useState(defaultColor);

  useOutside(() => {
    setColorPicker(false);
  }, wrapperRef);

  useEffect(() => {
    setPlanet((prevState) => ({...prevState, ...data}));

    return () => {
      setPlanet(DEFAULT_PLANET_FIELDS);
    };
  }, [data]);

  const previewStyle = useMemo(() => {
    let lineWidth = parseFloat(planet?.lineWidth) || 1;
    if(planet?.orb) {
      lineWidth += parseFloat(planet?.orb);
    }

    let lineStyle = tvChartGetStyleByNumber(planet.lineStyle);

    return `${lineWidth}px ${lineStyle} ${planet.lineColor}`
  }, [planet])

  const handleChange = (name, formatter) => (evt) => {
    let value = typeof formatter === "function" ? formatter(evt.target.value) : evt.target.value;
    let updatedPlanet = {
      ...planet,
      [name]: value
    }
    setPlanet(updatedPlanet);
    onChange(updatedPlanet);
  };

  const handleColorChange = useCallback(debounce((color) => {
    handleChange("lineColor")({
      target: { value: color },
    })
  }, 200), [planet]);

  if (!planet) return <></>;

  return (
    <Modal
      show={planetPopup}
      onHide={handlePlanetPop("")}
      backdrop="static"
      keyboard={false}
      centered
      className="stockData PlanetPopup"
    >
      <Modal.Body>
        <div className="topHeader d-flex align-items-start justify-content-between pb-2">
          <div className="left">
            <div
              className="label px-2 py-1 rounded d-flex align-items-center justify-content-center"
              style={{ background: "#F2F2F2" }}
            >
              <span className="me-1">{icons[planet.name]}</span>

              <p className="m-0 fw-sbold text-capitalize">{planet.name}</p>
            </div>
          </div>

          <Button
            onClick={handlePlanetPop("")}
            className="border-0 p-0"
            variant="transparent"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M14.34 -0.000732422C17.73 -0.000732422 20 2.37927 20 5.91927V14.0903C20 17.6203 17.73 19.9993 14.34 19.9993H5.67C2.28 19.9993 0 17.6203 0 14.0903V5.91927C0 2.37927 2.28 -0.000732422 5.67 -0.000732422H14.34ZM13.01 6.97027C12.67 6.62927 12.12 6.62927 11.77 6.97027L10 8.74927L8.22 6.97027C7.87 6.62927 7.32 6.62927 6.98 6.97027C6.64 7.31027 6.64 7.87027 6.98 8.20927L8.76 9.99027L6.98 11.7603C6.64 12.1103 6.64 12.6603 6.98 12.9993C7.15 13.1693 7.38 13.2603 7.6 13.2603C7.83 13.2603 8.05 13.1693 8.22 12.9993L10 11.2303L11.78 12.9993C11.95 13.1803 12.17 13.2603 12.39 13.2603C12.62 13.2603 12.84 13.1693 13.01 12.9993C13.35 12.6603 13.35 12.1103 13.01 11.7703L11.23 9.99027L13.01 8.20927C13.35 7.87027 13.35 7.31027 13.01 6.97027Z"
                fill="#EB5757"
              />
            </svg>
          </Button>
        </div>

        <div className="cardBody py-3">
          <Form>
            <Row>
              <Col md="4" sm="6" className="my-2">
                <label htmlFor="" className="form-label text-dark">
                  Select Aspect
                </label>

                <Form.Select
                  aria-label="Default select example"
                  className="form-control"
                  value={planet.aspect}
                  onChange={handleChange("aspect")}
                >
                  {Object.entries(PLANET_ASPECTS).map(([aspectKey, aspectName]) => (
                    <option key={aspectKey} value={aspectKey}>{aspectName}</option>
                  ))}
                </Form.Select>
              </Col>

              <Col md="4" sm="6" className="my-2">
                <label htmlFor="" className="form-label text-dark">
                  Select Direction
                </label>

                <Form.Select
                  aria-label="Default select example"
                  className="form-control"
                  value={planet.direction}
                  onChange={handleChange("direction")}
                >
                  <option value="forward">Forward</option>
                  <option value="reverse">Reverse</option>
                  <option value="both">Both</option>
                </Form.Select>
              </Col>

              <Col md="4" sm="6" className="my-2">
                <label htmlFor="" className="form-label text-dark">
                  Enter Orb
                </label>

                <input
                  type="number"
                  placeholder="0.0"
                  className="form-control"
                  value={planet.orb}
                  onChange={handleChange("orb")}
                />
              </Col>

              <Col md="4" sm="6" className="my-2">
                <label htmlFor="" className="form-label text-dark">
                  Line Style
                </label>

                <Form.Select
                  aria-label="Default select example"
                  className="form-control"
                  value={planet.lineStyle}
                  onChange={handleChange("lineStyle", (value) => parseFloat(value))}
                >
                  <option value="0">Solid</option>
                  <option value="1">Dotted</option>
                  <option value="2">Dashed</option>
                </Form.Select>
              </Col>

              <Col md="4" sm="6" className="my-2">
                <label className="form-label text-dark">Line Width</label>

                <input
                  type="number"
                  placeholder="1"
                  className="form-control"
                  value={planet.lineWidth}
                  onChange={handleChange("lineWidth")}
                />
              </Col>

              <Col lg="12" className="my-2">
                <div className="d-flex gap-10">
                  <label htmlFor="" className="form-label text-dark">
                    Line Colour
                  </label>
                  <div
                    className="chosen-color"
                    title="Using format option"
                    style={{ cursor: "pointer", backgroundColor: planet.lineColor, display: "inline-block" }}
                    onClick={() => setColorPicker((prevState) => !prevState)}
                  />
                </div>

                <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center gap-10 colorList">
                  {PLANET_LINE_COLORS.map((color, index) => {
                    return (
                      <li className={`planet-line-color ${planet.lineColor === color ? "active" : ""}`}>
                        <div
                          className="btnClr"
                          style={{ backgroundColor: color }}
                          onClick={() =>
                            handleChange("lineColor")({
                              target: { value: color },
                            })
                          }
                        />
                      </li>
                    )
                  })}
                </ul>

                <div
                  ref={wrapperRef}
                  className="color-selection my-2"
                  style={{ zIndex: showColorPicker ? 1 : -10 }}
                >
                  {showColorPicker && (
                    <ColorPicker
                      saturationHeight={100}
                      value={color}
                      onDrag={(color) => {
                        setColor(color);
                        handleColorChange(color);
                      }}
                    />
                  )}
                </div>
              </Col>
              <Col md="4" sm="6" className="my-2">
                <label htmlFor="" className="form-label text-dark">
                  Preview
                </label>
                <div
                  style={{
                    borderBottom: previewStyle
                  }}
                  className="planet-line-preview"
                ></div>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PlanetPop;
